import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import {
  assetData,
  countryList,
  type CustomerType,
  type ProductLine,
  type Site,
  type ProductFamily,
  type ProductModel,
} from 'views/epiroc/lib/assetData';
import {
  type ThrottleEventsObjectInterface,
  type GroupdedOverspeeding,
  type GroupdedOverspeedingEvent,
} from 'views/epiroc/MainPage/components/MachineEvents';

export interface HourlyAssetSensortCategoryDistribution {
  ts_hour: string;
  epoch_seconds: number;
  asset: string;
  field_name: string;
  value_category: string;
  percentage: number;
}

export interface AssetGearCounts {
  asset: string;
  sensor: string;
  gear: number;
  gear_count: number;
}

export interface AssetGearTime {
  asset: string;
  gear: number;
  total_time_in_sec: number;
}

export interface epirocMainPageState {
  data: {
    movingAverage: any;
    distribution: any;
    distributionEnv: any;
    distributionMachine: any;
    distributionOperator: any;
    movingAverageSensor: any;
    actualMovingAverageSensor: any;
    actualMovingAverageLastFetched: any;
    overSpeeding: any;
    overSpeedingEvents: any;

    breakingEvents: GroupdedOverspeedingEvent[];
    breakingData: GroupdedOverspeeding[];
    throttlePositionData: ThrottleEventsObjectInterface[];

    movingAverageMachine: any;
    movingAverageEnv: any;
    movingAverageOperator: any;
    prettoMovingAverageAcross: any;
    prettoMovingAverageAcrossMachine: any;
    prettoMovingAverageAcrossEnv: any;
    prettoMovingAverageAcrossOperator: any;

    prettoMovingAverage: any;
    prettoMovingAverageMachine: any;
    prettoMovingAverageEnv: any;
    prettoMovingAverageOperator: any;
    sensors: Array<{ id: string; display: string }>;
    selectedAssetsToGroup: string[];

    hourlyAssetSensorCategoryDistribution: HourlyAssetSensortCategoryDistribution[];
    granularSensorsData: Record<string, any>;

    gearSelectCounts: AssetGearCounts[];
    gearTimePerAsset: AssetGearTime[];
  };
  filters: {
    startDate: string;
    endDate: string;
    assetOptions: any;
    selectedAssets: string[];
    machineInFocus: string;
    machineToCompare: string;
    selectedCountry: string;
    selectedCustomer: CustomerType;
    selectedSite: Site;
    selectedProductLine: ProductLine;
    selectedProductFamily: ProductFamily;
    selectedProductModel: ProductModel;
  };
  loadingStates: {
    isLoadingMainPage: boolean;
    isLoadingParetoData: boolean;
    isLoadingMovingAverageAcross: boolean;
    isLoadingDistribution: boolean;
    isLoadingActualMovingAverageSensor: boolean;
    isLoadingOverSpeedingEventsPerDay: boolean;
    isLoadingOverSpeeding: boolean;
    isLoadingBreakingEvents: boolean;
    isLoadingThrottlePositionData: boolean;
    isLoadingGranularSensorsData: boolean;
    isLoadingGearSelectCounts: boolean;
    isLoadingGearTimePerAsset: boolean;
  };
  configurations: {
    countryList: string[];
    customerList: CustomerType[];
    siteList: Site[];
    productLineList: ProductLine[];
    productFamilyList: ProductFamily[];
    productModelList: ProductModel[];
    assetOptions: typeof assetData;
  };
}

const initialState: epirocMainPageState = {
  data: {
    prettoMovingAverageAcross: undefined,
    prettoMovingAverageAcrossOperator: undefined,
    prettoMovingAverageAcrossEnv: undefined,
    prettoMovingAverageAcrossMachine: undefined,

    prettoMovingAverage: undefined,
    prettoMovingAverageOperator: undefined,
    prettoMovingAverageEnv: undefined,
    prettoMovingAverageMachine: undefined,

    movingAverage: undefined,
    distribution: undefined,
    distributionEnv: undefined,
    distributionMachine: undefined,
    distributionOperator: undefined,

    movingAverageSensor: undefined,
    actualMovingAverageSensor: undefined,
    actualMovingAverageLastFetched: undefined,
    overSpeeding: undefined,
    overSpeedingEvents: undefined,

    breakingEvents: [],
    breakingData: [],

    throttlePositionData: [],

    movingAverageMachine: undefined,
    movingAverageEnv: undefined,
    movingAverageOperator: undefined,
    sensors: JSON.parse(localStorage.getItem('epiroc/sensors') ?? '[]'),
    selectedAssetsToGroup: [],
    // Array.from(
    //   new Set(
    //     assetData
    //       ?.filter(
    //         (asset) =>
    //           asset.country === 'Australia' &&
    //           asset.customer === 'Byrnecut' &&
    //           asset.product_line === 'Golden Grove' &&
    //           asset.product_line === 'Material handling' &&
    //           asset.product_family === 'Minetruck'
    //       )
    //       ?.map((asset) => asset.asset)
    //   )
    // ),

    hourlyAssetSensorCategoryDistribution: [],
    granularSensorsData: {},

    gearSelectCounts: [],
    gearTimePerAsset: [],
  },
  filters: {
    startDate: dayjs('2025-03-01').format('YYYY-MM-DD'),
    endDate: dayjs('2025-03-02').format('YYYY-MM-DD'),
    assetOptions: [],
    selectedAssets: [],
    machineInFocus: '8997632100',
    machineToCompare: '8997636600',
    selectedCountry: 'Australia',
    selectedCustomer: 'Byrnecut',
    selectedSite: 'Golden Grove',
    selectedProductLine: 'Material handling',
    selectedProductFamily: 'Minetruck',
    selectedProductModel: 'MT65',
  },
  loadingStates: {
    isLoadingMainPage: true,
    isLoadingParetoData: true,
    isLoadingMovingAverageAcross: true,
    isLoadingDistribution: true,
    isLoadingActualMovingAverageSensor: true,
    isLoadingOverSpeedingEventsPerDay: true,
    isLoadingOverSpeeding: true,
    isLoadingBreakingEvents: true,
    isLoadingThrottlePositionData: true,
    isLoadingGranularSensorsData: true,
    isLoadingGearSelectCounts: true,
    isLoadingGearTimePerAsset: true,
  },
  configurations: {
    countryList,
    customerList: Array.from(
      new Set(
        assetData
          ?.filter((asset) => asset.country === 'Australia')
          ?.map((asset) => asset.customer)
      )
    ),
    siteList: Array.from(
      new Set(
        assetData
          ?.filter(
            (asset) =>
              asset.country === 'Australia' && asset.customer === 'Byrnecut'
          )
          ?.map((asset) => asset.site)
      )
    ),
    productLineList: Array.from(
      new Set(
        assetData
          ?.filter(
            (asset) =>
              asset.country === 'Australia' &&
              asset.customer === 'Byrnecut' &&
              asset.site === 'Golden Grove'
          )
          ?.map((asset) => asset.product_line)
      )
    ),
    productFamilyList: Array.from(
      new Set(
        assetData
          ?.filter(
            (asset) =>
              asset.country === 'Australia' &&
              asset.customer === 'Byrnecut' &&
              asset.site === 'Golden Grove' &&
              asset.product_line === 'Material handling'
          )
          ?.map((asset) => asset.product_family)
      )
    ),
    productModelList: Array.from(
      new Set(
        assetData
          ?.filter(
            (asset) =>
              asset.country === 'Australia' &&
              asset.customer === 'Byrnecut' &&
              asset.site === 'Golden Grove' &&
              asset.product_line === 'Material handling' &&
              asset.product_family === 'Minetruck'
          )
          ?.map((asset) => asset.product_model)
      )
    ),
    assetOptions: Array.from(
      new Set(
        assetData?.filter(
          (asset) =>
            asset.country === 'Australia' &&
            asset.customer === 'Byrnecut' &&
            asset.site === 'Golden Grove' &&
            asset.product_line === 'Material handling' &&
            asset.product_family === 'Minetruck' &&
            asset.product_model === 'MT65'
        )
      )
    ),
  },
};

export const epirocMainPageSlice = createSlice({
  name: 'epirocmainpage',
  initialState,
  reducers: {
    setGearTimePerAsset(state, { payload }) {
      state.data.gearTimePerAsset = payload;
    },
    setGearSelectCounts(state, { payload }) {
      state.data.gearSelectCounts = payload;
    },
    setFilters(state, { payload }) {
      state.filters = {
        ...state.filters,
        ...payload,
      };
    },
    setEndDate(state, { payload }) {
      state.filters.endDate = payload;
    },
    setStartDate(state, { payload }) {
      console.log({ payload });
      state.filters.startDate = payload;
    },
    setMachineInFocus(state, { payload }) {
      state.filters.machineInFocus = payload;
    },
    setMachineToCompare(state, { payload }) {
      state.filters.machineToCompare = payload;
    },
    setPrettoMovingAverageAcross(state, { payload }) {
      state.data.prettoMovingAverageAcross = payload;
    },
    setPrettoMovingAverageAcrossOperator(state, { payload }) {
      state.data.prettoMovingAverageAcrossOperator = payload;
    },
    setPrettoMovingAverageAcrossEnv(state, { payload }) {
      state.data.prettoMovingAverageAcrossEnv = payload;
    },
    setPrettoMovingAverageAcrossMachine(state, { payload }) {
      state.data.prettoMovingAverageAcrossMachine = payload;
    },
    setPrettoMovingAverage(state, { payload }) {
      state.data.prettoMovingAverage = payload;
    },
    setPrettoMovingAverageOperator(state, { payload }) {
      state.data.prettoMovingAverageOperator = payload;
    },
    setPrettoMovingAverageEnv(state, { payload }) {
      state.data.prettoMovingAverageEnv = payload;
    },
    setPrettoMovingAverageMachine(state, { payload }) {
      state.data.prettoMovingAverageMachine = payload;
    },
    setMovingAverage(state, { payload }) {
      state.data.movingAverage = payload;
    },
    setMovingAverageOperator(state, { payload }) {
      state.data.movingAverageOperator = payload;
    },
    setMovingAverageEnv(state, { payload }) {
      state.data.movingAverageEnv = payload;
    },
    setMovingAverageMachine(state, { payload }) {
      state.data.movingAverageMachine = payload;
    },
    setOverSpeeding(state, { payload }) {
      state.data.overSpeeding = payload;
    },
    setOverSpeedingEvents(state, { payload }) {
      state.data.overSpeedingEvents = payload;
    },

    setBreakingEvents(state, { payload }) {
      state.data.breakingEvents = payload;
    },

    setBreakingData(state, { payload }) {
      state.data.breakingData = payload;
    },

    setMovingAverageSensor(state, { payload }) {
      state.data.movingAverageSensor = payload;
    },
    setActualMovingAverageSensor(state, { payload }) {
      state.data.actualMovingAverageSensor = payload;
    },
    setActualMovingAverageSensorLastFetched(state, { payload }) {
      state.data.actualMovingAverageLastFetched = payload;
    },
    setDistribution(state, { payload }) {
      state.data.distribution = payload;
    },
    setDistributionEnv(state, { payload }) {
      state.data.distributionEnv = payload;
    },
    setDistributionMachine(state, { payload }) {
      state.data.distributionMachine = payload;
    },
    setDistributionOperator(state, { payload }) {
      state.data.distributionOperator = payload;
    },
    setIsLoading(state, { payload }) {
      state.loadingStates = {
        ...state.loadingStates,
        ...payload,
      };
    },
    setSensors(state, { payload }) {
      state.data.sensors = payload;
    },
    setThrottlePositionData(state, { payload }) {
      state.data.throttlePositionData = payload;
    },
    setSelectedAssetsToGroup(state, { payload }) {
      console.log('payload', payload);
      state.data.selectedAssetsToGroup = payload;
    },
    setHourlyAssetSensorCategoryDistribution(state, { payload }) {
      state.data.hourlyAssetSensorCategoryDistribution = payload;
    },
    setGranularSensorsData(state, { payload }) {
      state.data.granularSensorsData = {
        ...state.data.granularSensorsData,
        ...payload,
      };
    },
    setSelectedCountry(state, { payload }) {
      state.data.selectedAssetsToGroup = [];
      state.filters.selectedCountry = payload;
      const customerList = assetData
        .filter((asset) => asset.country === payload)
        .map((asset) => asset.customer);
      state.filters.selectedCustomer = customerList[0];
      state.configurations.customerList = Array.from(new Set(customerList));
      const siteList = assetData
        .filter(
          (asset) =>
            asset.country === payload && customerList.includes(asset.customer)
        )
        .map((asset) => asset.site);
      state.configurations.siteList = Array.from(new Set(siteList));
      state.filters.selectedSite = siteList[0];
      const productLineList = assetData
        .filter(
          (asset) =>
            asset.country === payload &&
            customerList.includes(asset.customer) &&
            siteList.includes(asset.site)
        )
        .map((asset) => asset.product_line);
      state.configurations.productLineList = Array.from(
        new Set(productLineList)
      );
      state.filters.selectedProductLine = productLineList[0];
      const productFamilyList = assetData
        .filter(
          (asset) =>
            asset.country === payload &&
            customerList.includes(asset.customer) &&
            siteList.includes(asset.site) &&
            productLineList.includes(asset.product_line)
        )
        .map((asset) => asset.product_family);
      state.configurations.productFamilyList = Array.from(
        new Set(productFamilyList)
      );
      state.filters.selectedProductFamily = productFamilyList[0];
      const assetList = assetData
        .filter(
          (asset) =>
            asset.country === payload &&
            customerList.includes(asset.customer) &&
            siteList.includes(asset.site) &&
            productLineList.includes(asset.product_line) &&
            productFamilyList.includes(asset.product_family)
        )
        .map((asset) => asset);
      state.configurations.assetOptions = Array.from(new Set(assetList));
      state.filters.machineInFocus = assetList[0]?.asset;
      state.filters.machineToCompare =
        assetList[1]?.asset || assetList[0]?.asset;
    },
    setSelectedCustomer(state, { payload }) {
      state.data.selectedAssetsToGroup = [];
      state.filters.selectedCustomer = payload;
      const siteList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === payload
        )
        .map((asset) => asset.site);
      state.configurations.siteList = Array.from(new Set(siteList));
      state.filters.selectedSite = siteList[0];
      const productLineList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === payload
        )
        .map((asset) => asset.product_line);
      state.configurations.productLineList = Array.from(
        new Set(productLineList)
      );
      state.filters.selectedProductLine = productLineList[0];
      const productFamilyList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === payload
        )
        .map((asset) => asset.product_family);
      state.configurations.productFamilyList = Array.from(
        new Set(productFamilyList)
      );
      state.filters.selectedProductFamily = productFamilyList[0];
      const assetList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === payload
        )
        .map((asset) => asset);
      state.configurations.assetOptions = Array.from(new Set(assetList));
      state.filters.machineInFocus = assetList[0]?.asset;
      state.filters.machineToCompare =
        assetList[1]?.asset || assetList[0]?.asset;
    },
    setSelectedSite(state, { payload }) {
      state.data.selectedAssetsToGroup = [];
      state.filters.selectedSite = payload;
      const productLineList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === state.filters.selectedCustomer &&
            asset.site === payload
        )
        .map((asset) => asset.product_line);
      state.configurations.productLineList = Array.from(
        new Set(productLineList)
      );
      state.filters.selectedProductLine = productLineList[0];
      const productFamilyList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === state.filters.selectedCustomer &&
            asset.site === payload
        )
        .map((asset) => asset.product_family);
      state.configurations.productFamilyList = Array.from(
        new Set(productFamilyList)
      );
      state.filters.selectedProductFamily = productFamilyList[0];
      const assetList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === state.filters.selectedCustomer &&
            asset.site === payload
        )
        .map((asset) => asset);
      state.configurations.assetOptions = Array.from(new Set(assetList));
      state.filters.machineInFocus = assetList[0]?.asset;
      state.filters.machineToCompare =
        assetList[1]?.asset || assetList[0]?.asset;
    },
    setSelectedProductLine(state, { payload }) {
      state.data.selectedAssetsToGroup = [];
      state.filters.selectedProductLine = payload;
      const productFamilyList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === state.filters.selectedCustomer &&
            asset.site === state.filters.selectedSite &&
            asset.product_line === payload
        )
        .map((asset) => asset.product_family);
      state.configurations.productFamilyList = Array.from(
        new Set(productFamilyList)
      );
      state.filters.selectedProductFamily = productFamilyList[0];
      const assetList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === state.filters.selectedCustomer &&
            asset.site === state.filters.selectedSite &&
            asset.product_line === payload
        )
        .map((asset) => asset);
      state.configurations.assetOptions = Array.from(new Set(assetList));
      state.filters.machineInFocus = assetList[0]?.asset;
      state.filters.machineToCompare =
        assetList[1]?.asset || assetList[0]?.asset;
    },
    setSelectedProductFamily(state, { payload }) {
      state.data.selectedAssetsToGroup = [];
      state.filters.selectedProductFamily = payload;
      const assetList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === state.filters.selectedCustomer &&
            asset.site === state.filters.selectedSite &&
            asset.product_line === state.filters.selectedProductLine &&
            asset.product_family === payload
        )
        .map((asset) => asset);
      state.configurations.assetOptions = Array.from(new Set(assetList));
      state.filters.machineInFocus = assetList[0]?.asset;
      state.filters.machineToCompare =
        assetList[1]?.asset || assetList[0]?.asset;
    },
    setSelectedProductModel(state, { payload }) {
      state.data.selectedAssetsToGroup = [];
      state.filters.selectedProductModel = payload;
      const assetList = assetData
        .filter(
          (asset) =>
            asset.country === state.filters.selectedCountry &&
            asset.customer === state.filters.selectedCustomer &&
            asset.site === state.filters.selectedSite &&
            asset.product_line === state.filters.selectedProductLine &&
            asset.product_family === state.filters.selectedProductFamily &&
            asset.product_model === payload
        )
        .map((asset) => asset);
      state.configurations.assetOptions = Array.from(new Set(assetList));
      state.filters.machineInFocus = assetList[0]?.asset;
      state.filters.machineToCompare =
        assetList[1]?.asset || assetList[0]?.asset;
    },
    setConfigurations(
      state,
      {
        payload,
      }: { payload: { key: keyof typeof state.configurations; value: any } }
    ) {
      state.configurations = {
        ...state.configurations,
        [payload.key]: payload.value,
      };
    },
  },
});

export const {
  setGearTimePerAsset,
  setGearSelectCounts,
  setFilters,
  setEndDate,
  setStartDate,
  setMachineInFocus,
  setMachineToCompare,
  setMovingAverage,
  setMovingAverageEnv,
  setMovingAverageMachine,
  setMovingAverageOperator,
  setOverSpeeding,
  setOverSpeedingEvents,
  setBreakingEvents,
  setBreakingData,
  setMovingAverageSensor,
  setActualMovingAverageSensor,
  setActualMovingAverageSensorLastFetched,
  setDistribution,
  setDistributionEnv,
  setDistributionMachine,
  setDistributionOperator,
  setIsLoading,
  setPrettoMovingAverageAcross,
  setPrettoMovingAverageAcrossMachine,
  setPrettoMovingAverageAcrossEnv,
  setPrettoMovingAverageAcrossOperator,
  setPrettoMovingAverage,
  setPrettoMovingAverageMachine,
  setPrettoMovingAverageEnv,
  setPrettoMovingAverageOperator,
  setSensors,
  setThrottlePositionData,
  setSelectedAssetsToGroup,
  setHourlyAssetSensorCategoryDistribution,
  setGranularSensorsData,
  setConfigurations,
  setSelectedCountry,
  setSelectedCustomer,
  setSelectedSite,
  setSelectedProductLine,
  setSelectedProductFamily,
  setSelectedProductModel,
} = epirocMainPageSlice.actions;
export default epirocMainPageSlice.reducer;
